import {
  AutoComplete,
  FormInput,
  ISelectType,
  SwitchInput,
  useFetch,
  useFormContext,
} from "@4uhub/lib4uhub";
import { Grid, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { WhatsappTemplateAutomation } from "./WhatsappTemplateAutomation";
import { useParams } from "react-router-dom";
import { useWatch } from "react-hook-form";
import { TWhatsappTemplateForm } from "./whatsappTemplateSchema";
import MainApi from "../../../services/mainApi.service";
import FormInformation from "../../../components/FormInformation/FormInformation";
import { listTemplatesInternalTemplates } from "../../../services/ticketAutomationInternalTemplateTemplate.service";
import { IWhatsappTemplateDataProps } from "./models";

const TICKET_SERVICE_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketServices/";
const ticketService = new MainApi<ISelectType>(TICKET_SERVICE_ROUTE);

const GENERIC_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const genericService = new MainApi<ISelectType>(GENERIC_ROUTE);

const translationPath = "chatBot.page.templates.whatsapp_template.";

export const WhatsappTemplateData = memo(
  ({ status }: IWhatsappTemplateDataProps) => {
    const { t } = useTranslation();

    const { id } = useParams();

    const [ticketServicesOptions, setTicketServicesOptions] = useState<
      ISelectType[]
    >([]);

    const { setValue, getValues, control } =
      useFormContext<TWhatsappTemplateForm>();

    const templateTicketService = getValues("ticketService");

    const { sendRequest } = useFetch(ticketService.list);

    const ticketAutomation = useWatch({
      control,
      name: "ticketAutomation",
    });

    const fetchTicketServices = useCallback(async () => {
      const { data, success } = await sendRequest(null);
      if (data && success) {
        if (data.length === 1) {
          setValue("ticketService", data[0]);
        }
        setTicketServicesOptions(data);
      }
    }, [sendRequest, setValue]);

    const whatsappTemplateMessageCategoriesIdentifier = useMemo(() => {
      return { Identifier: "WhatsappTemplateMessageCategories" };
    }, []);

    const ticketAutomationInternalTemplateIdentifier = useMemo(() => {
      return { Identifier: "" };
    }, []);

    useEffect(() => {
      fetchTicketServices();
    }, [fetchTicketServices]);

    return (
      <Grid item container spacing={2} sm={12}>
        <Grid
          item
          xs={12}
          sm={12}
          textAlign={"end"}
          sx={
            id && status
              ? {
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }
              : undefined
          }
        >
          {id && status && (
            <FormInformation
              label={t(translationPath + "status")}
              data={status.name}
            />
          )}
          <SwitchInput label={t(translationPath + "enable")} name="enable" />
        </Grid>

        <Grid item xs={12} sm={ticketServicesOptions.length === 0 ? 6 : 12}>
          <FormInput
            label={t(translationPath + "name")}
            name="name"
            size="small"
            autoFocus
            fullWidth
          />
        </Grid>

        <WhatsappTemplateAutomation />

        {ticketServicesOptions.length === 0 && (
          <Grid item xs={12} sm={12}>
            <Typography variant="subtitle2" color="error">
              {t(translationPath + "no_ticket_service")}
            </Typography>
          </Grid>
        )}

        {ticketServicesOptions.length === 1 && (
          <Grid item xs={12} sm={6}>
            <FormInformation
              label={t(translationPath + "ticket_service")}
              data={ticketServicesOptions[0].name}
              minHeight={40}
            />
          </Grid>
        )}

        {id && templateTicketService && (
          <Grid item xs={12} sm={6}>
            <FormInformation
              label={t(translationPath + "ticket_service")}
              data={templateTicketService.name}
              minHeight={40}
            />
          </Grid>
        )}

        {!id && ticketServicesOptions.length > 1 && (
          <Grid item xs={12} sm={6}>
            <AutoComplete
              name="ticketService"
              label={t(translationPath + "ticket_service")}
              options={ticketServicesOptions}
              getOptionLabel={(option) => option.name}
              size="small"
              fullWidth
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <AutoComplete
            params={whatsappTemplateMessageCategoriesIdentifier}
            name="whatsMetaCategory"
            label={t(translationPath + "category")}
            getOptionLabel={(option) => option.name}
            size="small"
            fullWidth
            clearIcon={false}
            request={genericService.list}
          />
        </Grid>
        {ticketAutomation && ticketAutomation.code === "8" && (
          <Grid item xs={12} sm={6}>
            <AutoComplete
              params={ticketAutomationInternalTemplateIdentifier} // Adding an any value to make filter work
              size="small"
              name="ticketAutomationInternalTemplate"
              label={t(translationPath + "internal_template")}
              getOptionLabel={({ name }) => name}
              request={listTemplatesInternalTemplates}
              disable={!!id}
            />
          </Grid>
        )}
      </Grid>
    );
  },
);
